<template>
  <div class="setup-container" aria-label="Core Configuration Setup Page">
    <v-btn id="button-save-setup" v-if="showStickySaveButton" color="success" size="large" variant="elevated"
      @click="Update" :loading="dialog" :disabled="dialog" class="save-action-button sticky-save-btn mr-3" rounded="lg">
      <v-img :src="newSaveImg" alt="Save Configuration" class="image-icon-style"></v-img>
      {{ $t('saveConfiguration') }}
    </v-btn>
    <v-container fluid>

      <!-- Configuration Cards -->
      <div class="config-grid">

        <v-row no-gutters class="mb-4" align="center">
          <v-col cols="12" md="6">
            <h1 style="font-weight: 600; font-size: 28px;" class="mr-4">{{ $t('_CoreConfigurations') }}</h1>
          </v-col>
          <v-col cols="12" md="6" class="d-flex flex-wrap justify-end gap-2">

            <v-btn color="success" size="large" variant="elevated" @click="Update" :loading="dialog" :disabled="dialog"
              class="save-action-button">
              <template v-slot:prepend>
                <v-img :src="newSaveImg" alt="Reprocess User Dialog" class="image-icon-style"></v-img>
              </template>
              {{ $t('saveConfiguration') }}
            </v-btn>

          </v-col>
        </v-row>

        <!-- GPT Configuration Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.gptConfig }]" elevation="0"
          aria-label="GPT Configuration Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.gptConfig = !expandedCards.gptConfig"
            :aria-expanded="expandedCards.gptConfig" aria-controls="gpt-config-content">
            <div class="header-left">
              <img :src="imagesIcon.gptConfigIcon" alt="GPT Config" class="header-icon" />
              <span class="header-title-core-config">{{ $t('gptConfiguration') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.gptConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.gptConfig" id="gpt-config-content" class="card-content mt-3">

              <!-- Azure OpenAI Endpoints Section -->
              <div class="gpt-config-section">
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="storage-section-header">
                    <img :src="imagesGPTConfigCard.azureOpenAIIcon" alt="Azure OpenAI Endpoints"
                      class="storage-section-icon" />
                    <span class="storage-section-title">{{ $t('azureOpenAIEndpoints') }}</span>
                  </div>
                  <!-- Add Endpoint Button -->
                  <v-btn outlined @click="Add()" class="add-endpoint-btn"
                    v-show="setupListAzureOpenAIEndpoint.length > 0">
                    <v-icon location="left">mdi-plus</v-icon>
                    {{ $t('addEndpoint') }}
                  </v-btn>
                </div>

                <div v-for="(item, index) in setupListAzureOpenAIEndpoint" :key="item.id" class="endpoint-card">
                  <v-row no-gutters>
                    <v-col cols="12" md="2">
                      <div class="entra-field-label">
                        {{ $t('priority') }} <span style="color: #C32E0E;">*</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" class="ml-1"
                              color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoEndpointPriority') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.priorityDisplay" :rules="[rules.required]" density="comfortable"
                        variant="outlined" @keypress="isNumberpositive($event)" class="priority-field"
                        :placeholder="$t('enterPriority')">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="px-2">
                      <div class="entra-field-label">
                        {{ $t('urlConfig') }} <span style="color: #C32E0E;">*</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" class="ml-1"
                              color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoURLConfigCollection') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.azureOpenAIUrl" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="https://your-resource.openai.azure.com"
                        @blur="NormalizeAzureOpenAIUrl(index)"
                        @paste="$nextTick(() => NormalizeAzureOpenAIUrl(index))">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="px-2">
                      <div class="entra-field-label">
                        {{ $t('_API_Key') }} <span style="color: #C32E0E;">*</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" class="ml-1"
                              color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoAPIKeyGrant') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.azureOpenAIAPIKey" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.showValue ? 'text' : 'password'" :placeholder="$t('enterAPIKey')">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.showValue = !item.showValue">
                            <v-icon size="small">{{
                              item.showValue ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-center justify-end" style="padding-top: 24px;">
                      <v-btn v-if="index !== 0" icon color="error" variant="text" density="comfortable"
                        @click="Delete(index)">
                        <v-img :src="deleteIcon" width="20" height="20" />
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Embedding Configuration Section -->
              <hr style="border-color: #C9C9C9; color: #C9C9C9;" class="my-6" />
              <div class="gpt-config-section">
                <div class="storage-section-header mb-4">
                  <img :src="imagesGPTConfigCard.embeddingConfigIcon" alt="Embedding" class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('embeddingConfiguration') }}</span>
                </div>

                <v-row class="pb-0">
                  <v-col cols="12" sm="12" md="4">
                    <div class="sync-sso-card" aria-label="Embedding Configuration toggle"
                      :style="setup.EmbLoadBalanceMode == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                      <div class="sync-sso-header">
                        <span class="sync-sso-title">{{ $t('embeddingConfiguration') }}</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Embedding Configuration information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoEmbeddingLoadBalance') }}</span>
                        </v-tooltip>
                      </div>
                      <div class="sync-sso-content">
                        <span class="sync-sso-label">{{ $t('embeddingLoadBalance') }} : {{ setup.EmbLoadBalanceMode == '0' ? $t('off') : $t('on') }}</span>
                        <v-switch v-model="setup.EmbLoadBalanceMode" true-value="1" false-value="0" hide-details inset
                          density="compact"
                          :class="['sso-toggle', setup.EmbLoadBalanceMode == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                          aria-label="Toggle Embedding Configuration"></v-switch>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12" md="4" class="pb-0">
                    <div class="entra-field-label">
                      {{ $t('embeddingAPIVersion') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoEmbeddingAPIVersion') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.AzureOpenAIEmbeding_APIVersion" :rules="[rules.required]"
                      density="comfortable" variant="outlined" placeholder="2023-05-15">
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="4" class="pb-0">
                    <div class="entra-field-label">
                      {{ $t('embeddingType') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoEmbeddingType') }}</span>
                      </v-tooltip>
                    </div>
                    <v-autocomplete id="embedingType-setup" :items="azureOpenAI_EmbedingType_List" item-title="text"
                      item-value="value" v-model="setup.AzureOpenAI_EmbedingType" :rules="[rules.required]"
                      density="comfortable" variant="outlined" :placeholder="$t('selectEmbeddingType')">
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <v-row v-show="setup.EmbLoadBalanceMode == '0'">
                  <!-- setupListAzureOpenAIEndpoint.length > 0 -->
                  <v-col cols="12" md="6" class="pb-0">
                    <div class="entra-field-label">
                      {{ $t('embeddingURL') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoEmbeddingURL') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.AzureOpenAIEmbeding_Url" :rules="[rules.required]"
                      density="comfortable" variant="outlined"
                      placeholder="https://your-embedding-resource.openai.azure.com">
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6" class="mt-0">
                    <div class="entra-field-label">
                      {{ $t('embeddingAPIKey') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoEmbeddingAPIKey') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.AzureOpenAIEmbeding_APIKey" :rules="[rules.required]"
                      density="comfortable" variant="outlined"
                      :type="setup.Show_AzureOpenAI_APIKey ? 'text' : 'password'" :placeholder="$t('enterEmbeddingAPIKey')">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureOpenAI_APIKey =
                          !setup.Show_AzureOpenAI_APIKey
                          ">
                          <v-icon small>{{
                            setup.Show_AzureOpenAI_APIKey
                              ? "mdi-eye-outline" : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- GPT Mode Configuration Section -->
              <hr style="border-color: #C9C9C9; color: #C9C9C9;" class="mb-6" />
              <div class="gpt-config-section">
                <div class="storage-section-header mb-4">
                  <img :src="imagesGPTConfigCard.gptConfigIcon" alt="GPT Mode Configuration"
                    class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('gptModeConfiguration') }}</span>
                </div>

                <v-row>
                  <!-- GPT Mode Switch Card -->
                  <v-col cols="12" md="4">
                    <div class="sync-sso-card" aria-label="GPT Mode toggle"
                      :style="setup.GPTMode == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                      <div class="sync-sso-header">
                        <span class="sync-sso-title">{{ $t('gptMode') }}</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="GPT Mode information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoGPTMode') }}</span>
                        </v-tooltip>
                      </div>
                      <div class="sync-sso-content">
                        <span class="sync-sso-label">{{ $t('gptMode') }} : {{ setup.GPTMode == '0' ? $t('priority') : $t('fast') }}</span>
                        <v-switch v-model="setup.GPTMode" true-value="1" false-value="0" hide-details inset
                          density="compact"
                          :class="['sso-toggle', setup.GPTMode == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                          aria-label="Toggle GPT Mode"></v-switch>
                      </div>
                    </div>
                  </v-col>
                  <!-- Repeat Time Field -->
                  <v-col cols="12" md="4" v-show="setup.GPTMode == '1'">
                    <div class="entra-field-label">
                      {{ $t('repeatTimeMinutes') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoRepeatTime') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.GPTRepeatTime" @keypress="isNumber($event)" required
                      density="comfortable" variant="outlined" :rules="[rules.required]"
                      :placeholder="$t('enterRepeatTime')">
                    </v-text-field>
                  </v-col>
                  <!-- Model Field -->
                  <v-col cols="12" md="4" v-show="setup.GPTMode == '1'">
                    <div class="entra-field-label">
                      {{ $t('model') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoModelConfigured') }}</span>
                      </v-tooltip>
                    </div>
                    <v-autocomplete :items="DDModelShow" item-title="modelDisplay" item-value="modelName"
                      density="comfortable" variant="outlined" clearable v-model="setup.Model" :rules="[rules.required]"
                      required :placeholder="$t('selectModel')">
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <!-- Test of Questions Textarea -->
                <v-row v-show="setup.GPTMode == '1'" class="mt-2">
                  <v-col cols="12">
                    <div class="entra-field-label">
                      {{ $t('testOfQuestions') }} <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoTestOfQuestions') }}</span>
                      </v-tooltip>
                    </div>
                    <v-textarea id="Questions" density="comfortable" variant="outlined" counter auto-grow rows="6"
                      v-model="setup.Questions" :maxlength="1000" :rules="[rules.required]" required
                      :placeholder="$t('enterTestQuestions')">
                    </v-textarea>
                  </v-col>
                </v-row>

                <!-- Data Table -->
                <v-data-table v-show="setup.GPTMode == '1'" :items="listAzureEndpointFastMode" :headers="headers"
                  hide-default-footer :items-per-page="listAzureEndpointFastMode.length" class="mt-4">
                  <template v-slot:no-data>
                    <div class="d-flex align-center justify-center">{{ $t('noDataAvailable') }}</div>
                  </template>
                  <template v-slot:item="{ item }">
                    <tr>
                      <td style="text-align: center">{{ item.priority }}</td>
                      <td>{{ item.azureOpenAIUrl }}</td>
                      <td>{{ item.responseTime }}</td>
                      <td>{{ item.replytext }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </div>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Storage Configuration Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.storageConfig }]"
          elevation="0" aria-label="Storage Configuration Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.storageConfig = !expandedCards.storageConfig"
            :aria-expanded="expandedCards.storageConfig" aria-controls="storage-config-content">
            <div class="header-left">
              <img :src="imagesIcon.storageIcon" alt="Storage Config" class="header-icon" />
              <span class="header-title-core-config">{{ $t('storageConfiguration') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.storageConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.storageConfig" id="storage-config-content" class="card-content mt-3">
              <!-- Azure Blob Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.AzureBlobStorageIcon" alt="Azure Blob Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('azureBlobStorage') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('storageConnection') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoStorageConnectionBlob') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageConnection" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageConnection ? 'text' : 'password'" density="comfortable"
                      variant="outlined" :placeholder="$t('enterStorageConnection')">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageConnection =
                          !setup.Show_AzureStorageConnection
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageConnection
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('containerName') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoContainerNameBlob') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainer" :rules="[rules.required]" density="comfortable"
                      variant="outlined" :placeholder="$t('enterContainerName')">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- PDF Vector Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.PDFVectorStorage" alt="PDF Vector Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('pdfVectorStorage') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('storageConnection') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoStorageConnectionPDFVector') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageConnectionPDFVector" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageConnectionPDFVector ? 'text' : 'password'" density="comfortable"
                      variant="outlined" :placeholder="$t('enterStorageConnection')">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageConnectionPDFVector =
                          !setup.Show_AzureStorageConnectionPDFVector
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageConnectionPDFVector
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('containerName') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoContainerNamePDFVector') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainerPDFVector" :rules="[rules.required]"
                      density="comfortable" variant="outlined" :placeholder="$t('enterContainerName')">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Image Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.ImageStorage" alt="Storage" class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('imageStorage') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('storageConnection') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoStorageConnectionImage') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageConnectionImage" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageConnectionImage ? 'text' : 'password'" density="comfortable"
                      variant="outlined" :placeholder="$t('enterStorageConnection')">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageConnectionImage =
                          !setup.Show_AzureStorageConnectionImage
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageConnectionImage
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('containerName') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoContainerNameImage') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainerImage" :rules="[rules.required]" density="comfortable"
                      variant="outlined" :placeholder="$t('enterContainerName')">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Speech Document Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.SpeechDocStorage" alt="Speech Document Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('speechDocumentStorage') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('storageConnection') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoStorageConnectionSpeechDoc') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageSpeech" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageSpeech ? 'text' : 'password'" density="comfortable"
                      variant="outlined" :placeholder="$t('enterStorageConnection')">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageSpeech =
                          !setup.Show_AzureStorageSpeech
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageSpeech
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('containerName') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoContainerNameSpeechDoc') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainerSpeech" :rules="[rules.required]" density="comfortable"
                      variant="outlined" :placeholder="$t('enterContainerName')">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Quick Reply Icon Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.QuickReplyIconStorage" alt="Quick Reply Icon Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">{{ $t('quickReplyIconStorage') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('storageConnection') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoStorageConnectionQuickReply') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureQuickReplyIconStorage" :rules="[rules.required]"
                      :type="setup.Show_AzureQuickReplyIconStorage ? 'text' : 'password'" density="comfortable"
                      variant="outlined" :placeholder="$t('enterStorageConnection')">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureQuickReplyIconStorage =
                          !setup.Show_AzureQuickReplyIconStorage
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureQuickReplyIconStorage
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('containerName') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoContainerNameQuickReply') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureQuickReplyIconContainer" :rules="[rules.required]"
                      density="comfortable" variant="outlined" :placeholder="$t('enterContainerName')">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Language Services Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.languageServices }]"
          elevation="0" aria-label="Language Services Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.languageServices = !expandedCards.languageServices"
            :aria-expanded="expandedCards.languageServices" aria-controls="language-services-content">
            <div class="header-left">
              <img :src="imagesIcon.languageIcon" alt="Language Services" class="header-icon" />
              <span class="header-title-core-config">{{ $t('languageServices') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.languageServices ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.languageServices" id="language-services-content" class="card-content">
              <!-- Custom Language Services Tabs with Icons -->
              <div class="sso-tabs-container" aria-label="Language Services tabs">
                <div :class="['sso-tab-item', { 'sso-tab-active': activeLanguageTab === 0 }]"
                  @click="activeLanguageTab = 0" role="tab" :aria-selected="activeLanguageTab === 0" tabindex="0"
                  @keydown.enter="activeLanguageTab = 0" @keydown.space.prevent="activeLanguageTab = 0">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': activeLanguageTab === 0 }]">
                    <img :src="imagesLanguageServiceCard.TranslatorIcon" alt="Translator" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': activeLanguageTab === 0 }" />
                  </div>
                  <span
                    :class="['sso-tab-label', { 'sso-tab-label-active': activeLanguageTab === 0 }]">{{ $t('translator') }}</span>
                </div>
                <div :class="['sso-tab-item', { 'sso-tab-active': activeLanguageTab === 1 }]"
                  @click="activeLanguageTab = 1" role="tab" :aria-selected="activeLanguageTab === 1" tabindex="0"
                  @keydown.enter="activeLanguageTab = 1" @keydown.space.prevent="activeLanguageTab = 1">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': activeLanguageTab === 1 }]">
                    <img :src="imagesLanguageServiceCard.LanguageServiceIcon" alt="Language Service"
                      class="sso-tab-icon" :class="{ 'sso-tab-icon-white': activeLanguageTab === 1 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': activeLanguageTab === 1 }]">{{ $t('languageService') }}</span>
                </div>
                <div :class="['sso-tab-item', { 'sso-tab-active': activeLanguageTab === 2 }]"
                  @click="activeLanguageTab = 2" role="tab" :aria-selected="activeLanguageTab === 2" tabindex="0"
                  @keydown.enter="activeLanguageTab = 2" @keydown.space.prevent="activeLanguageTab = 2">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': activeLanguageTab === 2 }]">
                    <img :src="imagesLanguageServiceCard.SpeechToTextIcon" alt="Speech to Text" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': activeLanguageTab === 2 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': activeLanguageTab === 2 }]">{{ $t('speechToText') }}</span>
                </div>
              </div>

              <v-window v-model="activeLanguageTab" class="mt-4">
                <!-- Translator Tab -->
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('endpoint') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Endpoint information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoLanguageEndpoint') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageTranslatorEndpoint" :rules="[rules.required]"
                        density="comfortable" variant="outlined" :placeholder="$t('enterEndpointURL')">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('_API_Key') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="API Key information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoAPIKeyGrant') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageTranslatorKey" :rules="[rules.required]"
                        :type="setup.Show_LanguageTranslatorKey ? 'text' : 'password'" density="comfortable"
                        variant="outlined" :placeholder="$t('enterAPIKey')">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_LanguageTranslatorKey =
                            !setup.Show_LanguageTranslatorKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_LanguageTranslatorKey
                                ? "mdi-eye-outline"
                                : "mdi-eye-off-outline"
                            }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('location') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Location information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoLocationTranslator') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageTranslatorLocation" :rules="[rules.required]"
                        density="comfortable" variant="outlined" :placeholder="$t('enterLocation')">
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>

                <!-- Language Service Tab -->
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('languageEndpoint') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Language Endpoint information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoLanguageEndpoint') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageEndpoint" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterLanguageEndpoint')">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('languageKey') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Language Key information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoAPIKeyGrant') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageKey" :rules="[rules.required]"
                        :type="setup.Show_LanguageKey ? 'text' : 'password'" density="comfortable" variant="outlined"
                        :placeholder="$t('enterLanguageKey')">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_LanguageKey = !setup.Show_LanguageKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_LanguageKey ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>

                <!-- Speech to Text Tab -->
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('storageConnection') }} <span
                            class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Storage Connection information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoStorageConnectionSpeechToText') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.speechToTextKey" :rules="[rules.required]"
                        :type="setup.Show_SpeechToTextKey ? 'text' : 'password'" density="comfortable"
                        variant="outlined" :placeholder="$t('enterStorageConnection')">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_SpeechToTextKey =
                            !setup.Show_SpeechToTextKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_SpeechToTextKey
                                ? "mdi-eye-outline"
                                : "mdi-eye-off-outline"
                            }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('speechToTextRegion') }} <span
                            class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Speech to Text Region information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoSpeechToTextRegion') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.speechToTextRegion" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterRegion')">
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('speechToTextLanguage') }} <span
                            class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Speech to Text Language information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoSpeechToTextLanguage') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.speechToTextLanguage" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterSpeechToTextLanguage')">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('ffmpegPath') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="FFmpeg Path information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoFFmpegPath') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.pathFFMpeg" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterFFmpegPath')">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('ffmpegLocalPath') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="FFmpeg Local Path information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoFFmpegLocalPath') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.pathFFMpegLocal" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterFFmpegLocalPath')">
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Mail Config -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.mailConfig }]" elevation="0"
          aria-label="Mail Configuration Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.mailConfig = !expandedCards.mailConfig"
            :aria-expanded="expandedCards.mailConfig" aria-controls="mail-config-content">
            <div class="header-left">
              <img :src="imagesIcon.mailIcon" alt="Mail Config" class="header-icon" />
              <span class="header-title-core-config">{{ $t('mailConfig') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.mailConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.mailConfig" id="mail-config-content" class="card-content">
              
              <!-- Mail Provider & Method Selection -->
              <v-row class="mt-1">
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('emailSendingMethod') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoEmailSendingMethod') }}</span>
                    </v-tooltip>
                  </div>
                  <v-select v-model="setup.emailSendingMethod" :items="emailSendingMethodList" item-title="text"
                    item-value="value" density="comfortable" variant="outlined" :placeholder="$t('selectSendingMethod')">
                  </v-select>
                </v-col>

                <v-col cols="12" md="4" v-if="setup.emailSendingMethod === 'SMTP'">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('smtpProvider') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoSMTPProvider') }}</span>
                    </v-tooltip>
                  </div>
                  <v-select v-model="setup.smtpProvider" :items="smtpProviderList" item-title="text" item-value="value"
                    density="comfortable" variant="outlined" :placeholder="$t('selectSMTPProvider')">
                  </v-select>
                </v-col>

                <v-col cols="12" md="4" v-if="setup.emailSendingMethod === 'SMTP'">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('authenticationMethod') }}</span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoAuthenticationMethod') }}</span>
                    </v-tooltip>
                  </div>
                  <v-select v-model="setup.smtpAuthMethod" :items="smtpAuthMethodList" item-title="text" item-value="value"
                    density="comfortable" variant="outlined" :placeholder="$t('selectSMTPProvider')" :disabled="setup.smtpProvider === 'Gmail'">
                  </v-select>
                </v-col>
              </v-row>

              <!-- SMTP Basic Settings -->
              <div v-if="setup.emailSendingMethod === 'SMTP'">
                <hr style="border-color: #C9C9C9; color: #C9C9C9;" class="my-4" />
                <div class="storage-section-header mb-3">
                  <v-icon size="20" color="primary">mdi-server</v-icon>
                  <span class="storage-section-title">{{ $t('smtpServerSettings') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="3">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('smtpHost') }} <span class="required-asterisk">*</span></span>
                    </div>
                    <v-text-field v-model="setup.smtpHost" density="comfortable" variant="outlined"
                      :placeholder="setup.smtpProvider === 'Office365' ? 'smtp.office365.com' : 'smtp.gmail.com'"
                      :disabled="setup.smtpProvider !== 'Other'">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="2">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('smtpPort') }} <span class="required-asterisk">*</span></span>
                    </div>
                    <v-text-field v-model="setup.smtpPort" type="number" density="comfortable" variant="outlined"
                      placeholder="587" :disabled="setup.smtpProvider !== 'Other'">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="2">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('enableSSL') }}</span>
                    </div>
                    <v-switch v-model="setup.smtpEnableSsl" color="success" hide-details
                      :disabled="setup.smtpProvider !== 'Other'"
                      :class="['sso-toggle', setup.smtpEnableSsl ? 'sso-toggle--active' : 'sso-toggle--inactive']">
                    </v-switch>
                  </v-col>

                  <v-col cols="12" md="2">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('timeoutMs') }}</span>
                    </div>
                    <v-text-field v-model="setup.smtpTimeout" type="number" density="comfortable" variant="outlined"
                      placeholder="30000">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- From Email Settings -->
                <v-row>
                  <v-col cols="12" md="4">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('emailAddressFrom') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoEmailAddressFrom') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.emailHost" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="sender@example.com">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="4">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('displayName') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoDisplayName') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.hostName" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Your Company Name">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Basic Auth (Gmail / Other) -->
                <div v-if="setup.smtpAuthMethod !== 'OAuth2'">
                  <hr style="border-color: #C9C9C9; color: #C9C9C9;" class="my-4" />
                  <div class="storage-section-header mb-3">
                    <v-icon size="20" color="primary">mdi-lock</v-icon>
                    <span class="storage-section-title">{{ $t('basicAuthentication') }}</span>
                    <v-chip v-if="setup.smtpProvider === 'Gmail'" size="small" color="warning" class="ml-2">
                      {{ $t('requiresAppPassword') }}
                    </v-chip>
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('passwordAppPassword') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ setup.smtpProvider === 'Gmail' ? $t('_InfoGmailAppPassword') : $t('_InfoSMTPPassword') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.password" :rules="[rules.required]"
                        :type="setup.Show_Password ? 'text' : 'password'" density="comfortable" variant="outlined"
                        :placeholder="$t('enterPassword')">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_Password = !setup.Show_Password">
                            <v-icon size="small">{{ setup.Show_Password ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- OAuth2 Settings (Office 365) -->
                <div v-if="setup.smtpAuthMethod === 'OAuth2'">
                  <hr style="border-color: #C9C9C9; color: #C9C9C9;" class="my-4" />
                  <div class="storage-section-header mb-3">
                    <v-icon size="20" color="primary">mdi-shield-key</v-icon>
                    <span class="storage-section-title">{{ $t('oauth2Authentication') }}</span>
                    <v-chip size="small" color="error" class="ml-2">{{ $t('recommendedForOffice365') }}</v-chip>
                  </div>
                  <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                    <strong>Setup Guide:</strong> Create an App Registration in Azure AD with <code>Mail.Send</code> permission.
                    <a href="https://learn.microsoft.com/en-us/exchange/client-developer/legacy-protocols/how-to-authenticate-an-imap-pop-smtp-application-by-using-oauth" 
                       target="_blank" class="ml-1">Learn more</a>
                  </v-alert>
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('tenantId') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>Azure AD > Overview > Directory (tenant) ID</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.smtpOAuth2TenantId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        :type="setup.Show_SmtpOAuth2TenantId ? 'text' : 'password'">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_SmtpOAuth2TenantId = !setup.Show_SmtpOAuth2TenantId">
                            <v-icon size="small">{{ setup.Show_SmtpOAuth2TenantId ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('clientId') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>Azure AD > App Registration > Overview > Application (client) ID</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.smtpOAuth2ClientId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        :type="setup.Show_SmtpOAuth2ClientId ? 'text' : 'password'">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_SmtpOAuth2ClientId = !setup.Show_SmtpOAuth2ClientId">
                            <v-icon size="small">{{ setup.Show_SmtpOAuth2ClientId ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('clientSecret') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>Azure AD > App Registration > Certificates & secrets > New client secret</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.smtpOAuth2ClientSecret" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterClientSecret')"
                        :type="setup.Show_SmtpOAuth2ClientSecret ? 'text' : 'password'">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_SmtpOAuth2ClientSecret = !setup.Show_SmtpOAuth2ClientSecret">
                            <v-icon size="small">{{ setup.Show_SmtpOAuth2ClientSecret ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Mailgun Settings -->
              <div v-if="setup.emailSendingMethod === 'Mailgun'">
                <hr style="border-color: #C9C9C9; color: #C9C9C9;" class="my-4" />
                <div class="storage-section-header mb-3">
                  <v-icon size="20" color="primary">mdi-email-fast</v-icon>
                  <span class="storage-section-title">{{ $t('mailgunApiSettings') }}</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('mailgunApiURL') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoMailgunApiURL') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.mailgunApiUrl" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="https://api.mailgun.net/v3/your-domain/messages">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('mailgunApiKey') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoMailgunApiKey') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.mailgunApiKey" :rules="[rules.required]" density="comfortable"
                      variant="outlined" :placeholder="$t('enterMailgunApiKey')"
                      :type="setup.Show_MailgunApiKey ? 'text' : 'password'">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_MailgunApiKey = !setup.Show_MailgunApiKey">
                          <v-icon size="small">{{ setup.Show_MailgunApiKey ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">{{ $t('fromEmail') }} <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoFromEmail') }}</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.mailFrom" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Support <support@yourdomain.com>">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- System Settings Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.systemSettings }]"
          elevation="0" aria-label="System Settings Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.systemSettings = !expandedCards.systemSettings"
            :aria-expanded="expandedCards.systemSettings" aria-controls="system-settings-content">
            <div class="header-left">
              <img :src="imagesIcon.systemIcon" alt="System Settings" class="header-icon" />
              <span class="header-title-core-config">{{ $t('systemSettings') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.systemSettings ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.systemSettings" id="system-settings-content" class="card-content">
              <!-- First Row: 4 Switch Cards -->
              <v-row class="mt-1">
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Auto Stream toggle"
                    :style="setup.autoStream == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">{{ $t('autoStream') }}</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Auto Stream information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoAutoStream') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">{{ $t('autoStream') }} : {{ setup.autoStream == '1' ? $t('enabled') : $t('disabled') }}</span>
                      <v-switch v-model="setup.autoStream" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.autoStream == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Auto Stream"></v-switch>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Anonymous Mode toggle"
                    :style="setup.anonymous == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">{{ $t('anonymousMode') }}</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Anonymous Mode information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoAnonymousMode') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">{{ $t('anonymousMode') }} : {{ setup.anonymous == '1' ? $t('on') : $t('off') }}</span>
                      <v-switch v-model="setup.anonymous" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.anonymous == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Anonymous Mode"></v-switch>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Force Disable GPT toggle"
                    :style="setup.FlagDisableGPT == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">{{ $t('forceDisableGPT') }}</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Force Disable GPT information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoForceDisableGPT') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">{{ $t('forceDisableGPT') }} : {{ setup.FlagDisableGPT == '1' ? $t('on') : $t('off') }}</span>
                      <v-switch v-model="setup.FlagDisableGPT" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.FlagDisableGPT == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Force Disable GPT"></v-switch>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Auto Disable GPT toggle"
                    :style="setup.FlagAutoDisableGPT == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">{{ $t('autoDisableGPT') }}</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Auto Disable GPT information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoAutoDisableGPT') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">{{ $t('autoDisableGPT') }} : {{ setup.FlagAutoDisableGPT == '1' ? $t('on') : $t('off')
                        }}</span>
                      <v-switch v-model="setup.FlagAutoDisableGPT" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.FlagAutoDisableGPT == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Auto Disable GPT"></v-switch>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Second Row: Time Delay and Character Limit -->
              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('timeDelayMs') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Time Delay information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoTimeDelayMs') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.TimeDelayCharacter" @keypress="isNumber($event)" density="comfortable"
                    variant="outlined" :placeholder="$t('enterTimeDelay')">
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('characterLimit') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Character Limit information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoCharacterLimit') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.LimitedCharacter" @keypress="isNumber($event)" density="comfortable"
                    variant="outlined" :placeholder="$t('enterCharacterLimit')">
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Third Row: GPT Auto-Disable Period, Pricing, and Message -->
              <v-row>
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('gptAutoDisablePeriod') }} <span
                        class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="GPT Auto-Disable Period information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoGPTAutoDisablePeriod') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.AutoDisableGPTPeriodDate" @keypress="isNumber($event)"
                    density="comfortable" variant="outlined" :placeholder="$t('enterPeriodInDays')">
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('gptAutoDisablePricing') }} <span
                        class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="GPT Auto-Disable Pricing information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoGPTAutoDisablePricing') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.AutoDisableGPTPricing" @keypress="isNumber($event)" density="comfortable"
                    variant="outlined" :placeholder="$t('enterPricingThreshold')">
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">{{ $t('gptAutoDisableMessage') }} <span
                        class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="GPT Auto-Disable Message information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoGPTAutoDisableMessage') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.AutoDisableGPTMessage" density="comfortable" variant="outlined"
                    :placeholder="$t('enterAutoDisableMessage')">
                  </v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- other config -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.otherConfig }]" elevation="0"
          aria-label="Other Configuration Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.otherConfig = !expandedCards.otherConfig"
            :aria-expanded="expandedCards.otherConfig" aria-controls="other-config-content">
            <div class="header-left">
              <img :src="imagesIcon.otherIcon" alt="Other Config" class="header-icon" />
              <span class="header-title-core-config">{{ $t('otherConfig') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.otherConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.otherConfig" id="other-config-content" class="card-content">
              <!-- Custom Other Config Tabs with Icons -->
              <div class="sso-tabs-container" aria-label="Other configuration tabs">
                <div :class="['sso-tab-item', { 'sso-tab-active': otherTab === 0 }]" @click="otherTab = 0" role="tab"
                  :aria-selected="otherTab === 0" tabindex="0" @keydown.enter="otherTab = 0"
                  @keydown.space.prevent="otherTab = 0">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': otherTab === 0 }]">
                    <img :src="imagesOtherConfigCard.GoogleIcon" alt="Google Config" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': otherTab === 0 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': otherTab === 0 }]">{{ $t('googleConfig') }}</span>
                </div>
                <div :class="['sso-tab-item', { 'sso-tab-active': otherTab === 1 }]" @click="otherTab = 1" role="tab"
                  :aria-selected="otherTab === 1" tabindex="0" @keydown.enter="otherTab = 1"
                  @keydown.space.prevent="otherTab = 1">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': otherTab === 1 }]">
                    <img :src="imagesOtherConfigCard.AnthropicIcon" alt="Anthropic Config" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': otherTab === 1 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': otherTab === 1 }]">{{ $t('anthropicConfig') }}</span>
                </div>
              </div>

              <v-window v-model="otherTab" class="mt-4">
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <!-- Project ID Field with Label and Tooltip -->
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('projectID') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Project ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoProjectID') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.projectID" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterProjectID')">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <!-- Location ID Field with Label and Tooltip -->
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('locationID') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Location ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoLocationID') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.locationID" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :placeholder="$t('enterLocationID')">
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" sm="12" md="12" class="d-flex justify-end align-center mb-3 mr-3">
                      <div class="d-flex flex-row align-center px-3">
                        <v-icon class="mr-2" :disabled="!isUploadFileJSON" color="green" large>
                          mdi-check-circle </v-icon>
                        <span class="font-bold">{{ $t('jsonCredential') }}</span>
                      </div>

                      <v-tooltip location="top start" max-width="300">
                        <template #activator="{ props }">
                          <div v-bind="props">
                            <DragDropFile :dialog="true" :DragDrop="false" :ShowListFile="false"
                              :allowType="['application/json']" :accept="'application/json'"
                              @filesUploaded="uploadFile($event)" />
                          </div>
                        </template>
                        <span>{{ $t('uploadJSONOnly') }}</span>
                      </v-tooltip>
                      <span>{{ tempFileJSON != null && tempFileJSON != '' && tempFileJSON != undefined ?
                        tempFileJSON.name
                        :
                        '' }}</span>
                    </v-col>
                  </v-row>
                </v-window-item>

                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="12">
                      <!-- Anthropic API Key Field with Label and Tooltip -->
                      <div class="entra-field-label">
                        <span class="field-label-text">{{ $t('anthropicAPIKey') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Anthropic API Key information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoAnthropicAPIKey') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.amazonClaude_APIKey" :rules="[rules.required]"
                        :type="setup.Show_AmazonClaude_APIKey ? 'text' : 'password'" density="comfortable"
                        variant="outlined" :placeholder="$t('enterAnthropicAPIKey')">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_AmazonClaude_APIKey =
                            !setup.Show_AmazonClaude_APIKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_AmazonClaude_APIKey
                                ? "mdi-eye-outline"
                                : "mdi-eye-off-outline"
                            }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- single sign on -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.singleSignOn }]"
          elevation="0" aria-label="Single Sign-On Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.singleSignOn = !expandedCards.singleSignOn"
            :aria-expanded="expandedCards.singleSignOn" aria-controls="single-sign-on-content">
            <div class="header-left">
              <img :src="imagesIcon.ssoIcon" alt="Single Sign-On" class="header-icon" />
              <span class="header-title-core-config">{{ $t('singleSignOn') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.singleSignOn ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.singleSignOn" id="single-sign-on-content" class="card-content">
              <!-- Custom SSO Tabs with Icons -->
              <div class="sso-tabs-container" aria-label="Single Sign-On provider tabs">
                <div v-for="(item, index) in setupOpenIDConnectList" :key="index"
                  :class="['sso-tab-item', { 'sso-tab-active': signOnTab === index }]" @click="signOnTab = index"
                  role="tab" :aria-selected="signOnTab === index" tabindex="0" @keydown.enter="signOnTab = index"
                  @keydown.space.prevent="signOnTab = index">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': signOnTab === index }]">
                    <img :src="imagesSSOCard[item.provider + 'Icon']" :alt="item.provider" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': signOnTab === index }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': signOnTab === index }]">{{ item.provider
                    }}</span>
                </div>
              </div>

              <v-window v-model="signOnTab" class="mt-4 sso-window">
                <v-window-item v-for="(item, index) in setupOpenIDConnectList" :key="index">
                  <!-- SSO Switch Cards Row -->
                  <v-row class="mb-4">
                    <v-col cols="12" md="6">
                      <div class="sync-sso-card" aria-label="Single Sign-On toggle"
                        :style="item.sso ? 'background: #FFF2F2' : 'background: #EDEDED'">
                        <div class="sync-sso-header">
                          <span class="sync-sso-title">{{ $t('singleSignOn') }} {{ item.provider }}</span>
                          <v-tooltip location="top start" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                                aria-label="SSO information">mdi-information-outline</v-icon>
                            </template>
                            <span v-if="item.provider === 'Line'">{{ $t('_InfoSSOLine') }}</span>
                            <span v-else>{{ $t('_InfoSSOProvider', { provider: item.provider }) }}</span>
                          </v-tooltip>
                        </div>
                        <div class="sync-sso-content">
                          <span class="sync-sso-label">{{ $t('singleSignOn') }} {{ item.provider }} : {{ item.sso ? $t('open') :
                            $t('close')
                            }}</span>
                          <v-switch v-model="item.sso" hide-details inset density="compact"
                            :class="['sso-toggle', item.sso ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                            :aria-label="'Toggle Single Sign-On ' + item.provider"></v-switch>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="sync-sso-card" aria-label="Single Sign-On Chat toggle"
                        :style="item.ssoChat ? 'background: #FFF2F2' : 'background: #EDEDED'">
                        <div class="sync-sso-header">
                          <span class="sync-sso-title">{{ $t('singleSignOn') }} {{ item.provider }} {{ $t('inChat') }}</span>
                          <v-tooltip location="top start" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                                aria-label="SSO Chat information">mdi-information-outline</v-icon>
                            </template>
                            <span v-if="item.provider === 'Line'">{{ $t('_InfoSSOLineChat') }}</span>
                            <span v-else>{{ $t('_InfoSSOProviderChat', { provider: item.provider }) }}</span>
                          </v-tooltip>
                        </div>
                        <div class="sync-sso-content">
                          <span class="sync-sso-label">{{ $t('singleSignOn') }} {{ item.provider }} {{ $t('inChat') }} : {{ item.ssoChat ?
                            $t('open') :
                            $t('close') }}</span>
                          <v-switch v-model="item.ssoChat" hide-details inset density="compact"
                            :class="['sso-toggle', item.ssoChat ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                            :aria-label="'Toggle Single Sign-On ' + item.provider + ' in Chat'"></v-switch>
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Text Fields Row -->
                  <v-row>
                    <!-- Instance Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="Instance field">
                        <span class="field-label">{{ $t('instance') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Instance information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoInstance') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.instance" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :disabled="item.provider == 'Microsoft'" hide-details="auto"
                        aria-label="Instance input"></v-text-field>
                    </v-col>

                    <!-- Domain Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik' && item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="Domain field">
                        <span class="field-label">{{ $t('domain') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Domain information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoDomain') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.domain" :rules="[rules.required]" density="comfortable"
                        variant="outlined" hide-details="auto" aria-label="Domain input"></v-text-field>
                    </v-col>

                    <!-- Tenant ID Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik' && item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="Tenant ID field">
                        <span class="field-label">{{ $t('tenantId') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Tenant ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoTenantID') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.tenantId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_TenantId ? 'text' : 'password'" hide-details="auto"
                        aria-label="Tenant ID input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_TenantId = !item.Show_TenantId"
                            :aria-label="item.Show_TenantId ? 'Hide Tenant ID' : 'Show Tenant ID'">
                            <v-icon size="small">{{ item.Show_TenantId ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- Client ID Field -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="field-label-wrapper" aria-label="Client ID field">
                        <span class="field-label">{{ $t('clientId') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Client ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoClientID') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.clientId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_ClientId ? 'text' : 'password'" hide-details="auto"
                        aria-label="Client ID input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_ClientId = !item.Show_ClientId"
                            :aria-label="item.Show_ClientId ? 'Hide Client ID' : 'Show Client ID'">
                            <v-icon size="small">{{ item.Show_ClientId ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- Callback Path Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik'">
                      <div class="field-label-wrapper" aria-label="Callback Path field">
                        <span class="field-label">{{ $t('callbackPath') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Callback Path information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoCallbackPath') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.callbackPath" :rules="[rules.required]" density="comfortable"
                        variant="outlined" hide-details="auto" aria-label="Callback Path input"></v-text-field>
                    </v-col>

                    <!-- Client Secret Field -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="field-label-wrapper" aria-label="Client Secret field">
                        <span class="field-label">{{ $t('clientSecret') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Client Secret information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoClientSecret') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.clientSecret" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_ClientSecret ? 'text' : 'password'" hide-details="auto"
                        aria-label="Client Secret input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_ClientSecret = !item.Show_ClientSecret"
                            :aria-label="item.Show_ClientSecret ? 'Hide Client Secret' : 'Show Client Secret'">
                            <v-icon size="small">{{ item.Show_ClientSecret ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- API Scope Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik' && item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="API Scope field">
                        <span class="field-label">{{ $t('apiScope') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="API Scope information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoAPIScope') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.apiScope" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_APIScope ? 'text' : 'password'" hide-details="auto"
                        aria-label="API Scope input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_APIScope = !item.Show_APIScope"
                            :aria-label="item.Show_APIScope ? 'Hide API Scope' : 'Show API Scope'">
                            <v-icon size="small">{{ item.Show_APIScope ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- URL Config Field -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="field-label-wrapper" aria-label="URL Config field">
                        <span class="field-label">{{ $t('urlConfig') }} <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="URL Config information">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ $t('_InfoURLConfigSSO') }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.urlConfig" :rules="[rules.required]" density="comfortable"
                        variant="outlined" hide-details="auto" aria-label="URL Config input"></v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Microsoft Entra ID Group Configuration Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.microsoftEntra }]"
          elevation="0" aria-label="Microsoft Entra ID Group Configuration Section">
          <button type="button" class="expandable-card-header" @click="expandedCards.microsoftEntra = !expandedCards.microsoftEntra"
            :aria-expanded="expandedCards.microsoftEntra" aria-controls="microsoft-entra-content">
            <div class="header-left">
              <img :src="imagesIcon.microsoftIcon" alt="Microsoft Entra ID" class="header-icon" />
              <span class="header-title-core-config">{{ $t('microsoftEntraIDGroupConfig') }}</span>
            </div>
            <v-icon size="35" class="toggle-icon-btn">{{ expandedCards.microsoftEntra ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </button>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.microsoftEntra" id="microsoft-entra-content" class="card-content">
              <!-- Row 1: Tenant ID and Client ID -->
              <v-row>
                <v-col cols="12" md="6">
                  <div class="field-label-wrapper" aria-label="Tenant ID field">
                    <span class="field-label">{{ $t('tenantId') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Tenant ID information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoEntraTenantID') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.SecurityGroupTenantID" :rules="[rules.required]" density="comfortable"
                    variant="outlined" :type="setup.Show_SecurityGroupTenantID ? 'text' : 'password'"
                    hide-details="auto" aria-label="Tenant ID input">
                    <template v-slot:append-inner>
                      <v-btn icon size="small" variant="text" density="comfortable"
                        @click="setup.Show_SecurityGroupTenantID = !setup.Show_SecurityGroupTenantID"
                        :aria-label="setup.Show_SecurityGroupTenantID ? 'Hide Tenant ID' : 'Show Tenant ID'">
                        <v-icon size="small">{{ setup.Show_SecurityGroupTenantID ? "mdi-eye-outline" :
                          "mdi-eye-off-outline" }}</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="field-label-wrapper" aria-label="Client ID field">
                    <span class="field-label">{{ $t('clientId') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Client ID information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoClientID') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.SecurityGroupClientID" :rules="[rules.required]" density="comfortable"
                    variant="outlined" :type="setup.Show_SecurityGroupClientID ? 'text' : 'password'"
                    hide-details="auto" aria-label="Client ID input">
                    <template v-slot:append-inner>
                      <v-btn icon size="small" variant="text" density="comfortable"
                        @click="setup.Show_SecurityGroupClientID = !setup.Show_SecurityGroupClientID"
                        :aria-label="setup.Show_SecurityGroupClientID ? 'Hide Client ID' : 'Show Client ID'">
                        <v-icon size="small">{{ setup.Show_SecurityGroupClientID ? "mdi-eye-outline" :
                          "mdi-eye-off-outline" }}</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Row 2: Client Secret and Sync SSO Groups -->
              <v-row class="mt-4" align="end">
                <v-col cols="12" md="6">
                  <div class="field-label-wrapper" aria-label="Client Secret field">
                    <span class="field-label">{{ $t('clientSecret') }} <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Client Secret information">mdi-information-outline</v-icon>
                      </template>
                      <span>{{ $t('_InfoEntraClientSecret') }}</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.SecurityGroupClientSecrete" :rules="[rules.required]"
                    density="comfortable" variant="outlined"
                    :type="setup.Show_SecurityGroupClientSecrete ? 'text' : 'password'" hide-details="auto"
                    aria-label="Client Secret input">
                    <template v-slot:append-inner>
                      <v-btn icon size="small" variant="text" density="comfortable"
                        @click="setup.Show_SecurityGroupClientSecrete = !setup.Show_SecurityGroupClientSecrete"
                        :aria-label="setup.Show_SecurityGroupClientSecrete ? 'Hide Client Secret' : 'Show Client Secret'">
                        <v-icon size="small">{{ setup.Show_SecurityGroupClientSecrete ? "mdi-eye-outline" :
                          "mdi-eye-off-outline" }}</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="sync-sso-card" aria-label="Sync SSO Groups section"
                    :style="syncSSO ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">{{ $t('syncSSOGroups') }}</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Sync SSO Groups information">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('_InfoSyncSSOGroups') }}</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">{{ $t('syncSSOGroups') }} : {{ syncSSO ? $t('on') : $t('off') }}</span>
                      <v-switch v-model="syncSSO" hide-details inset density="compact"
                        :class="['sso-toggle', syncSSO ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Sync SSO Groups"></v-switch>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>
        <!-- ...existing other configuration cards with similar modern styling... -->
      </div>
    </v-container>

    <!-- Loading Dialog -->
    <v-dialog v-model="dialog" persistent width="320">
      <v-card class="loading-card">
        <v-card-text class="text-center py-6">
          <v-progress-circular indeterminate color="#EABD07" size="48" class="mb-4"></v-progress-circular>
          <div class="loading-text">{{ $t('savingConfiguration') }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LogTrace from "@/api/function.js";
import { mask } from "vue-the-mask";
import DragDropFile from "@/components/DragDropFile.vue";
import editImg from "@/assets/edit1.png";
import deleteImg from "@/assets/delete1.png";

// Config Card Icons
import gptConfigIcon from "@/assets/Icons/gptModelConfig.svg";
import storageIcon from "@/assets/Icons/storage-config.svg";
import languageIcon from "@/assets/Icons/language-service.svg";
import mailIcon from "@/assets/Icons/mail-config.svg";
import systemIcon from "@/assets/Icons/system-setting.svg";
import otherIcon from "@/assets/Icons/other-config.svg";
import ssoIcon from "@/assets/Icons/single-sign-on.svg";
import microsoftIcon from "@/assets/Icons/microsoft-entra.svg";
import LineIcon from "@/assets/Icons/Line.svg";
import MicrosoftIcon from "@/assets/Icons/Microsoft.svg";
import AuthentikIcon from "@/assets/Icons/Authentik.svg";
import GoogleIcon from "@/assets/Icons/Google.svg";
import AnthropicIcon from "@/assets/Icons/Anthropic.svg";
import TranslatorIcon from "@/assets/Icons/Translator.svg";
import LanguageServiceIcon from "@/assets/Icons/language-service-config.svg";
import SpeechToTextIcon from "@/assets/Icons/speech-to-text.svg";
import AzureBlobStorageIcon from "@/assets/Icons/AzureBlobStorage.svg";
import ImageStorage from "@/assets/Icons/ImageStorage.svg";
import PDFVectorStorage from "@/assets/Icons/PDFVectorStorage.svg";
import QuickReplyIconStorage from "@/assets/Icons/QuickReplyIcon.svg";
import SpeechDocStorage from "@/assets/Icons/SpeechDocStorage.svg";
import deleteIcon from "@/assets/Icons/Delete.png";
import embeddingConfigIcon from "@/assets/Icons/embeddingConfig.svg";
import azureOpenAIIcon from "@/assets/Icons/azure.svg";
import newSaveImg from "@/assets/Icons/white-save-floppy.svg";
import deleteAlertIcon from "@/assets/images/trash-solid.svg";

export default {
  name: "Setup",
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  components: { DragDropFile },
  mixins: [LogTrace],
  directives: {
    mask,
  },
  data: () => ({
    mask: "##########",
    showStickySaveButton: false,
    scrollThreshold: 145,
    items: [],
    panel: [0],
    activeLanguageTab: 0,
    otherTab: 0,
    signOnTab: 0,
    expandedCards: {
      gptConfig: false,
      storageConfig: false,
      languageServices: false,
      mailConfig: false,
      systemSettings: false,
      otherConfig: false,
      singleSignOn: false,
      microsoftEntra: false,
    },

    show1: false,
    formHasErrors: false,
    permission: {
      add: true,
      edit: true,
      delete: true,
    },
    mailcheck: false,
    phonecheck: false,
    filters: {
      username: [],
      email: [],
      employeeName: [],
      userType: [],
    },
    DDRole: [
      {
        name: "Admin",
        value: 1,
      },
      {
        name: "ผู้ใช้งานทั่วไป",
        value: 2,
      },
    ],
    // Mail Config Dropdowns
    emailSendingMethodList: [
      { text: "SMTP", value: "SMTP" },
      { text: "Mailgun", value: "Mailgun" },
    ],
    smtpProviderList: [
      { text: "Office 365 (OAuth2 Recommended)", value: "Office365" },
      { text: "Gmail (App Password)", value: "Gmail" },
      { text: "Other", value: "Other" },
    ],
    smtpAuthMethodList: [
      { text: "Basic Authentication", value: "Basic" },
      { text: "OAuth2 Authentication", value: "OAuth2" },
    ],
    numberRules: [(v) => !!v || "Input is required!"],
    rules: {
      required: (value) => !!value || "Required.",
      min: (v) => v.length >= 10 || "Min 10 numbers",
    },
    Email: null,
    gg: null,
    UserType: null,
    dialog: false,
    edit: {
      id: null,
      key: null,
      value: null,
    },
    user: {
      id: null,
      key: null,
      value: null,
    },

    Editdialog: false,
    Adddialog: false,
    isSearch: false,
    searchname: "",
    email: "",
    totalTable: 0,
    itemsPerPage: 10,
    searchTotalTable: 0,
    options: {},
    today: null,
    setupList: [],
    setupListAzureOpenAIEndpoint: [],
    DDModelShow: [],
    listAzureEndpointFastMode: [],
    setupOpenIDConnectList: [],
    setup: {
      azureOpenAI_URL: null,
      azureOpenAI_APIKey: null,
      azureStorageConnection: null,
      Show_AzureStorageConnection: false,
      azureContainer: null,
      azureStorageConnectionPDFVector: null,
      Show_AzureStorageConnectionPDFVector: false,
      azureContainerPDFVector: null,
      azureStorageConnectionImage: null,
      Show_AzureStorageConnectionImage: false,
      azureContainerImage: null,
      azureStorageSpeech: null,
      Show_AzureStorageSpeech: false,
      azureContainerSpeech: null,
      azureQuickReplyIconStorage: null,
      Show_AzureQuickReplyIconStorage: false,
      azureQuickReplyIconContainer: null,
      languageTranslatorKey: null,
      Show_LanguageTranslatorKey: false,
      languageTranslatorEndpoint: null,
      languageTranslatorLocation: null,
      languageKey: null,
      Show_LanguageKey: false,
      languageEndpoint: null,
      channelSecretInternal: null,
      channelTokenInternal: null,
      // Mail Config - Basic
      emailSendingMethod: null,
      smtpProvider: null,
      smtpAuthMethod: null,
      smtpHost: null,
      smtpPort: null,
      smtpEnableSsl: true,
      smtpTimeout: 30000,
      emailHost: null,
      hostName: null,
      password: null,
      Show_Password: false,
      // Mail Config - OAuth2 (Office 365)
      smtpOAuth2TenantId: null,
      Show_SmtpOAuth2TenantId: false,
      smtpOAuth2ClientId: null,
      Show_SmtpOAuth2ClientId: false,
      smtpOAuth2ClientSecret: null,
      Show_SmtpOAuth2ClientSecret: false,
      // Mail Config - Mailgun
      mailgunApiUrl: null,
      mailgunApiKey: null,
      Show_MailgunApiKey: false,
      mailFrom: null,
      openAIAPIKey: null,
      Show_OpenAI_APIKey: false,
      openAIUrl: null,
      speechToTextKey: null,
      Show_SpeechToTextKey: false,
      speechToTextRegion: null,
      speechToTextLanguage: null,
      pathFFMpeg: null,
      pathFFMpegLocal: null,
      autoStream: null,
      anonymous: null,
      TimeDelayCharacter: null,
      LimitedCharacter: null,
      AzureOpenAIEmbeding_Url: null,
      AzureOpenAIEmbeding_APIKey: null,
      Show_AzureOpenAI_APIKey: false,
      azureOpenAIEmbeding_APIVersion: null,
      AzureOpenAI_EmbedingType: null,
      projectID: null,
      locationID: null,
      fileJSON: null,
      amazonClaude_APIKey: null,
      Show_AmazonClaude_APIKey: false,
      GPTMode: "0",
      GPTRepeatTime: null,
      Model: null,
      Questions: null,
      SSO: null,
      SSOChat: null,
      instance: null,
      domain: null,
      tenantId: null,
      clientId: null,
      callbackPath: null,
      clientSecret: null,
      APIScope: null,
      URLConfig: null,
      SecurityGroupTenantID: null,
      Show_SecurityGroupTenantID: false,
      SecurityGroupClientID: null,
      Show_SecurityGroupClientID: false,
      SecurityGroupClientSecrete: null,
      Show_SecurityGroupClientSecrete: false,
      FlagAutoDisableGPT: null,
      FlagDisableGPT: null,
      AutoDisableGPTPeriodDate: null,
      AutoDisableGPTMessage: null,
      AutoDisableGPTPricing: null,
      EmbLoadBalanceMode: "0",
    },
    secretKey: "01234567890123456789012345678901",
    images: {
      edit: editImg,
      delete: deleteImg,
    },
    azureOpenAI_EmbedingType_List: [
      { text: "text-embedding-3-large", value: "text-embedding-3-large" },
      { text: "text-embedding-3-small", value: "text-embedding-3-small" },
      { text: "text-embedding-ada-002", value: "text-embedding-ada-002" },
    ],
    myClipboard: "",
    myClipboardFullSteam: "",
    urlApi: import.meta.env.VITE_APP_API,
    tempFileJSON: null,
    isUploadFileJSON: false,
    syncSSO: false,
    oldsetup: {
      SecurityGroupTenantID: null,
      SecurityGroupClientID: null,
      SecurityGroupClientSecrete: null,
    },
    imagesIcon: {
      gptConfigIcon: gptConfigIcon,
      storageIcon: storageIcon,
      languageIcon: languageIcon,
      mailIcon: mailIcon,
      systemIcon: systemIcon,
      otherIcon: otherIcon,
      ssoIcon: ssoIcon,
      microsoftIcon: microsoftIcon,
    },
    imagesSSOCard: {
      LineIcon: LineIcon,
      MicrosoftIcon: MicrosoftIcon,
      AuthentikIcon: AuthentikIcon,
    },
    imagesOtherConfigCard: {
      GoogleIcon: GoogleIcon,
      AnthropicIcon: AnthropicIcon,
    },
    imagesLanguageServiceCard: {
      TranslatorIcon: TranslatorIcon,
      LanguageServiceIcon: LanguageServiceIcon,
      SpeechToTextIcon: SpeechToTextIcon,
    },
    imagesStorageConfigCard: {
      AzureBlobStorageIcon: AzureBlobStorageIcon,
      ImageStorage: ImageStorage,
      PDFVectorStorage: PDFVectorStorage,
      QuickReplyIconStorage: QuickReplyIconStorage,
      SpeechDocStorage: SpeechDocStorage,
    },
    deleteIcon: deleteIcon,
    newSaveImg: newSaveImg,
    imagesGPTConfigCard: {
      embeddingConfigIcon: embeddingConfigIcon,
      azureOpenAIIcon: azureOpenAIIcon,
      gptConfigIcon: gptConfigIcon,
    },
  }),
  watch: {
    syncSSO: {
      handler(val) {
        if (val) {
          this.setup.SecurityGroupTenantID = this.oldsetup.SecurityGroupTenantID;
          this.setup.SecurityGroupClientID = this.oldsetup.SecurityGroupClientID;
          this.setup.SecurityGroupClientSecrete = this.oldsetup.SecurityGroupClientSecrete;
        }
      },
      deep: true,
      immediate: true,
    },
    // SMTP Provider watcher - auto-fill host/port when provider changes
    'setup.smtpProvider': {
      handler(val) {
        if (val === 'Office365') {
          this.setup.smtpHost = 'smtp.office365.com';
          this.setup.smtpPort = 587;
          this.setup.smtpEnableSsl = true;
        } else if (val === 'Gmail') {
          this.setup.smtpHost = 'smtp.gmail.com';
          this.setup.smtpPort = 587;
          this.setup.smtpEnableSsl = true;
          this.setup.smtpAuthMethod = 'Basic';
        }
        // 'Other' - keep user input
      },
    },
    // setupOpenIDConnectList: {
    //   handler(val) {
    //     let self = this;
    //     const newItem = val.find((item) => item.provider === "Microsoft");
    //     if (self.syncSSO == true) {
    //       self.setup.SecurityGroupTenantID = newItem.tenantId;
    //       self.setup.SecurityGroupClientID = newItem.clientId;
    //       self.setup.SecurityGroupClientSecrete = newItem.clientSecret;
    //     }
    //   },
    //   deep: true,
    // },
  },
  mounted() {
    this.GetDate();
    this.Get();
    this.GetDDLModelDataAzureProvider();
    this.myClipboard =
      `<script src="${this.urlApi}Snippet/ChatFrameworkSnippet.js"><` +
      "/script>";
    this.myClipboardFullSteam =
      `<script src="${this.urlApi}Snippet/ChatFrameworkSnippetFull.js"><` +
      "/script>";
    const scroller = document.querySelector('.v-main__scroller') || globalThis;
    scroller.addEventListener('scroll', this.handleScroll, true);
    this._scrollContainer = scroller;
  },
  unmounted() {
    // ลบ listener ออกจาก container ที่แท้จริง
    if (this._scrollContainer) {
      this._scrollContainer.removeEventListener('scroll', this.handleScroll, true);
    }
  },

  computed: {
    form() {
      return {
        id: this.user.id,
        key: this.user.key,
        value: this.user.value,
      };
    },
    headers() {
      return [
        {
          title: this.$t("no"),
          align: "center",
          value: "priority",
          sortable: false,
        },
        {
          title: "AzureOpenAIUrl",
          value: "azureOpenAIUrl",
          align: "left",
          sortable: true,
        },
        {
          title: this.$t("responseTimeMs"),
          value: "responseTime",
          align: "left",
          sortable: true,
        },
        { title: this.$t("Answers"), value: "replytext", align: "left", sortable: true },
      ];
    },
  },

  methods: {
    handleScroll(event) {
      const { scrollTop } = event.target;
      const target = event.target;
      if (target.closest('.v-navigation-drawer') ||
        target.closest('.v-app-bar') ||
        target.closest('.v-toolbar')) {
        return;
      }
      if (scrollTop >= this.scrollThreshold) {
        this.showStickySaveButton = true;
      }
      else {
        this.showStickySaveButton = false;
      }
    },
    Apply() {
      try {
        this.dialog = true;
        this.$call.GetSetupApply(
          (dataResponse) => {
            if (dataResponse.status == 0) {
              alert("Apply change");
            }
            this.dialog = false;
          },
          (errorResource) => {
            this.dialog = false;
            if (errorResource.status != 0) {
              console.error(errorResource);
            }
          }
        );
      } catch (error) {
        this.LogTrace(error, "Apply");
      }
    },
    setListAzureEndpointFastMode(data) {
      let newList = data.sort((a, b) => a.priority - b.priority);
      let checkfastmode = data.filter((item) => item.responseTime !== null);
      if (checkfastmode.length == newList.length) {
        this.listAzureEndpointFastMode = newList.map((item) => {
          return {
            priority: item.priority,
            azureOpenAIUrl: this.normalizeEndpointBaseUrl(item.azureOpenAIUrl),
            responseTime: item.responseTime == 0 ? "-" : item.responseTime,
            replytext: item.replytext,
          };
        });
      } else {
        this.listAzureEndpointFastMode = [];
      }
    },
    setListOpenIDConnect(data) {
      const providerOrder = { 'Line': 1, 'Microsoft': 2, 'Authentik': 3 };
      this.setupOpenIDConnectList = data.map((item) => {
        return {
          provider: item.provider,
          sso: item.sso,
          ssoChat: item.ssoChat,
          instance: item.instance,
          domain: item.domain,
          tenantId: item.tenantId,
          Show_TenantId: false,
          clientId: item.clientId,
          Show_ClientId: false,
          callbackPath: item.callbackPath,
          clientSecret: item.clientSecret,
          Show_ClientSecret: false,
          apiScope: item.apiScope,
          Show_APIScope: false,
          urlConfig: item.urlConfig,
        };
      }).sort((a, b) => {
        return (providerOrder[a.provider] || 999) - (providerOrder[b.provider] || 999);
      });
    },
    Get() {
      try {
        this.$call.GetSetupOverviewGeneral(
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.setupList = dataResponse.data.listDbsettings;
              if (dataResponse.data.listAzureOpenAIEndpoint.length == 0) {
                this.listAzureEndpointFastMode = [];
                this.Add();
              } else {
                this.setupListAzureOpenAIEndpoint =
                  dataResponse.data.listAzureOpenAIEndpoint.map((item) => ({
                    ...item,
                    azureOpenAIUrl: this.normalizeEndpointBaseUrl(item.azureOpenAIUrl),
                    showValue: false, // เพิ่ม key ใหม่ "showValue"
                  }));
                this.setListAzureEndpointFastMode(
                  dataResponse.data.listAzureOpenAIEndpoint
                );
              }

              if (dataResponse.data.listOpenIDConnect.length == 0) {
                this.AddOpenIDConnect("Microsoft");
                this.AddOpenIDConnect("Authentik");
                this.AddOpenIDConnect("Line");
              } else {
                this.setListOpenIDConnect(dataResponse.data.listOpenIDConnect);

                if (
                  this.setupOpenIDConnectList.filter(
                    (item) => item.provider == "Microsoft"
                  ).length == 0
                ) {
                  this.AddOpenIDConnect("Microsoft");
                } else if (
                  this.setupOpenIDConnectList.filter(
                    (item) => item.provider == "Authentik"
                  ).length == 0
                ) {
                  this.AddOpenIDConnect("Authentik");
                } else if (
                  this.setupOpenIDConnectList.filter(
                    (item) => item.provider == "Line"
                  ).length == 0
                ) {
                  this.AddOpenIDConnect("Line");
                }
              }

              this.MapSetupList(this.setupList, false);
            }
            this.dialog = false;
            this.isSearch = false;
          },
          (errorResource) => {
            this.dialog = false;
            this.isSearch = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "Get");
      }
    },
    MapSetupList(source, mapForSave) {
      source.forEach((element) => {
        if (this.mapSimpleSetupField(element, mapForSave)) {
          return;
        }
        if (this.mapFlagSetupField(element, mapForSave)) {
          return;
        }
        if (this.mapGptModeGatedSetupField(element, mapForSave)) {
          return;
        }
        if (this.mapSecurityGroupSetupField(element, mapForSave)) {
          return;
        }
        this.mapSpecialSetupField(element, mapForSave);
      });
    },
    // key -> setup property for the plain "mapForSave ? this.setup.X : element.value" pattern
    setupSimpleFieldMap() {
      return {
        AzureContainer: "azureContainer",
        AzureContainerPDFVector: "azureContainerPDFVector",
        AzureContainerImage: "azureContainerImage",
        AzureOpenAI_APIKey: "azureOpenAI_APIKey",
        AzureOpenAI_Url: "azureOpenAI_URL",
        AzureOpenAIEmbeding_APIKey: "AzureOpenAIEmbeding_APIKey",
        AzureOpenAIEmbeding_Url: "AzureOpenAIEmbeding_Url",
        AzureOpenAIEmbeding_APIVersion: "AzureOpenAIEmbeding_APIVersion",
        AzureOpenAI_EmbedingType: "AzureOpenAI_EmbedingType",
        AzureStorageConnection: "azureStorageConnection",
        AzureStorageConnectionPDFVector: "azureStorageConnectionPDFVector",
        AzureStorageConnectionImage: "azureStorageConnectionImage",
        AzureStorageSpeech: "azureStorageSpeech",
        AzureContainerSpeech: "azureContainerSpeech",
        AzureQuickReplyIconStorage: "azureQuickReplyIconStorage",
        AzureQuickReplyIconContainer: "azureQuickReplyIconContainer",
        EmailHost: "emailHost",
        HostName: "hostName",
        Password: "password",
        EmailSendingMethod: "emailSendingMethod",
        SmtpProvider: "smtpProvider",
        SmtpAuthMethod: "smtpAuthMethod",
        SmtpHost: "smtpHost",
        SmtpOAuth2TenantId: "smtpOAuth2TenantId",
        SmtpOAuth2ClientId: "smtpOAuth2ClientId",
        SmtpOAuth2ClientSecret: "smtpOAuth2ClientSecret",
        MailgunApiUrl: "mailgunApiUrl",
        MailgunApiKey: "mailgunApiKey",
        MailFrom: "mailFrom",
        LanguageEndpoint: "languageEndpoint",
        LanguageKey: "languageKey",
        LanguageTranslatorEndpoint: "languageTranslatorEndpoint",
        LanguageTranslatorKey: "languageTranslatorKey",
        LanguageTranslatorLocation: "languageTranslatorLocation",
        OpenAI_Url: "openAIUrl",
        OpenAI_APIKey: "openAIAPIKey",
        SpeechToTextKey: "speechToTextKey",
        SpeechToTextRegion: "speechToTextRegion",
        SpeechToTextLanguage: "speechToTextLanguage",
        PathFFMpeg: "pathFFMpeg",
        PathFFMpegLocal: "pathFFMpegLocal",
        TimeDelayCharacter: "TimeDelayCharacter",
        AutoDisableGPTPeriodDate: "AutoDisableGPTPeriodDate",
        AutoDisableGPTMessage: "AutoDisableGPTMessage",
        AutoDisableGPTPricing: "AutoDisableGPTPricing",
        ProjectID: "projectID",
        LocationID: "locationID",
        AmazonClaude_APIKey: "amazonClaude_APIKey",
        Instance: "instance",
        Domain: "domain",
        TenantId: "tenantId",
        ClientId: "clientId",
        CallbackPath: "callbackPath",
        ClientSecret: "clientSecret",
        APIScope: "APIScope",
        URLConfig: "URLConfig",
      };
    },
    mapSimpleSetupField(element, mapForSave) {
      const prop = this.setupSimpleFieldMap()[element.key];
      if (prop === undefined) {
        return false;
      }
      element.value = mapForSave ? this.setup[prop] : element.value;
      this.setup[prop] = element.value;
      return true;
    },
    // key -> setup property for the boolean "1"/"0" flag pattern
    setupFlagFieldMap() {
      return {
        AutoStream: "autoStream",
        Anonymous: "anonymous",
        FlagAutoDisableGPT: "FlagAutoDisableGPT",
        FlagDisableGPT: "FlagDisableGPT",
        GPTMode: "GPTMode",
        SSO: "SSO",
        SSOChat: "SSOChat",
        EmbeddingLoadBalanceEnable: "EmbLoadBalanceMode",
      };
    },
    mapFlagSetupField(element, mapForSave) {
      const prop = this.setupFlagFieldMap()[element.key];
      if (prop === undefined) {
        return false;
      }
      if (mapForSave) {
        element.value = this.setup[prop] == "1" ? "1" : "0";
      } else {
        this.setup[prop] = element.value == true || element.value == "1" ? "1" : "0"; //NOSONAR
      }
      return true;
    },
    // key -> setup property for fields gated to null when GPTMode is "0"
    mapGptModeGatedSetupField(element, mapForSave) {
      const gptModeGatedMap = {
        GPTRepeatTime: "GPTRepeatTime",
        Model: "Model",
        Questions: "Questions",
      };
      const prop = gptModeGatedMap[element.key];
      if (prop === undefined) {
        return false;
      }
      const gptModeIsOff = this.setup.GPTMode == "0";
      const savedValue = gptModeIsOff ? null : this.setup[prop];
      const currentValue = gptModeIsOff ? null : element.value;
      element.value = mapForSave ? savedValue : currentValue;
      this.setup[prop] = element.value;
      return true;
    },
    // key -> setup property for security-group fields that also sync oldsetup
    mapSecurityGroupSetupField(element, mapForSave) {
      const securityGroupMap = {
        SecurityGroupTenantID: "SecurityGroupTenantID",
        SecurityGroupClientID: "SecurityGroupClientID",
        SecurityGroupClientSecrete: "SecurityGroupClientSecrete",
      };
      const prop = securityGroupMap[element.key];
      if (prop === undefined) {
        return false;
      }
      element.value = mapForSave ? this.setup[prop] : element.value;
      this.setup[prop] = element.value;
      this.oldsetup[prop] = this.setup[prop];
      return true;
    },
    mapSpecialSetupField(element, mapForSave) {
      switch (element.key) {
        case "SmtpPort":
          this.mapSmtpPortField(element, mapForSave);
          break;
        case "SmtpEnableSsl":
          this.mapSmtpEnableSslField(element, mapForSave);
          break;
        case "SmtpTimeout":
          this.mapSmtpTimeoutField(element, mapForSave);
          break;
        case "LimitedCharacter":
          this.mapLimitedCharacterField(element, mapForSave);
          break;
        case "FileJSON":
          this.mapFileJSONField(element, mapForSave);
          break;
        case "SSOGroups":
          this.mapSSOGroupsField(element, mapForSave);
          break;
        default:
          break;
      }
    },
    mapSmtpPortField(element, mapForSave) {
      if (mapForSave) {
        element.value = this.setup.smtpPort ? String(this.setup.smtpPort) : "587";
      } else {
        this.setup.smtpPort = element.value ? Number.parseInt(element.value) : 587;
      }
    },
    mapSmtpEnableSslField(element, mapForSave) {
      if (mapForSave) {
        element.value = this.setup.smtpEnableSsl ? "1" : "0";
      } else {
        this.setup.smtpEnableSsl = element.value === true || element.value === "1" || element.value === "true";
      }
    },
    mapSmtpTimeoutField(element, mapForSave) {
      if (mapForSave) {
        element.value = this.setup.smtpTimeout ? String(this.setup.smtpTimeout) : "30000";
      } else {
        this.setup.smtpTimeout = element.value ? Number.parseInt(element.value) : 30000;
      }
    },
    mapLimitedCharacterField(element, mapForSave) {
      element.value = mapForSave
        ? this.setup.LimitedCharacter || "5000"
        : element.value;
      this.setup.LimitedCharacter = element.value;
    },
    mapFileJSONField(element, mapForSave) {
      element.value = mapForSave ? this.setup.fileJSON : element.value;
      this.setup.fileJSON = element.value;
      if (
        this.setup.fileJSON != null &&
        this.setup.fileJSON != "" &&
        this.setup.fileJSON != undefined
      ) {
        this.isUploadFileJSON = true;
      }
    },
    mapSSOGroupsField(element, mapForSave) {
      if (mapForSave) {
        element.value = this.syncSSO ? "1" : "0";
      } else {
        this.syncSSO = element.value == true || element.value == "1"; //NOSONAR
      }
    },
    EditUser(temp) {
      this.edit.id = temp.id;
      this.edit.key = temp.key;
      this.edit.value = temp.value;

      this.Editdialog = true;
    },
    normalizeEndpointBaseUrl(value) {
      if (value == null) {
        return value;
      }

      const trimmedValue = String(value).trim();
      if (trimmedValue == "") {
        return trimmedValue;
      }

      const normalizedProtocolValue = trimmedValue
        .replace(/^(https?):\/(?!\/)/i, "$1://")
        .replace(/^https\/\//i, "https://")
        .replace(/^http\/\//i, "http://");
      const valueWithProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(normalizedProtocolValue)
        ? normalizedProtocolValue
        : `https://${normalizedProtocolValue}`;

      try {
        const url = new URL(valueWithProtocol);
        return `${url.protocol}//${url.host}`;
      } catch (error) { //NOSONAR
        const match = normalizedProtocolValue.match(/^((?:https?:\/\/)?[^/?#]+)/i);
        return match ? match[1] : trimmedValue;
      }
    },
    NormalizeAzureOpenAIUrl(index) {
      const item = this.setupListAzureOpenAIEndpoint[index];
      if (!item) {
        return;
      }
      item.azureOpenAIUrl = this.normalizeEndpointBaseUrl(item.azureOpenAIUrl);
    },
    NormalizeAzureOpenAIEndpointList() {
      this.setupListAzureOpenAIEndpoint = this.setupListAzureOpenAIEndpoint.map((item) => ({
        ...item,
        azureOpenAIUrl: this.normalizeEndpointBaseUrl(item.azureOpenAIUrl),
      }));
    },
    ValidatesetupListAzureOpenAIEndpoint() {
      if (this.setupListAzureOpenAIEndpoint.length == 0) {
        return false;
      }
      for (const azureEndpointItem of this.setupListAzureOpenAIEndpoint) {
        let item = azureEndpointItem;
        if (
          item.priorityDisplay == 0 ||
          item.priorityDisplay == "" ||
          item.priorityDisplay == null ||
          item.azureOpenAIUrl == "" ||
          item.azureOpenAIUrl == null ||
          item.azureOpenAIAPIKey == "" ||
          item.azureOpenAIAPIKey == null
        ) {
          return false;
        }
      }
      return true;
    },
    ValidateFastMode() {
      if (this.setup.GPTMode != "1") {
        return true;
      }

      if (
        this.setup.GPTRepeatTime == "0" ||
        this.setup.GPTRepeatTime == 0 ||
        this.setup.GPTRepeatTime == null ||
        this.setup.GPTRepeatTime == undefined ||
        this.setup.Model == "" ||
        this.setup.Model == null ||
        this.setup.Model == undefined ||
        this.setup.Questions == "" ||
        this.setup.Questions == null ||
        this.setup.Questions == undefined
      ) {
        return false;
      }
      return true;
    },
    ValidateMailConfig() {
      if (
        this.setup.emailHost == "" ||
        this.setup.emailHost == null ||
        this.setup.emailHost == undefined ||
        this.setup.hostName == "" ||
        this.setup.hostName == null ||
        this.setup.hostName == undefined ||
        this.setup.smtpPort == "" ||
        this.setup.smtpPort == null ||
        this.setup.smtpPort == undefined ||
        this.setup.smtpHost == "" ||
        this.setup.smtpHost == null ||
        this.setup.smtpHost == undefined
      ) {
        return false;
      }

      if(this.setup.smtpAuthMethod == 'Basic') {
        if (
          this.setup.password == "" ||
          this.setup.password == null ||
          this.setup.password == undefined
        ) {
          return false;
        }
      } else if(this.setup.smtpAuthMethod == 'OAuth2') {
        if (
          this.setup.smtpOAuth2TenantId == "" ||
          this.setup.smtpOAuth2TenantId == null ||
          this.setup.smtpOAuth2TenantId == undefined ||
          this.setup.smtpOAuth2ClientId == "" ||
          this.setup.smtpOAuth2ClientId == null ||
          this.setup.smtpOAuth2ClientId == undefined ||
          this.setup.smtpOAuth2ClientSecret == "" ||
          this.setup.smtpOAuth2ClientSecret == null ||
          this.setup.smtpOAuth2ClientSecret == undefined
        ) {
          return false;
        }
      }
      return true;
    },
    Update() {
      try {
        this.NormalizeAzureOpenAIEndpointList();
        if (
          !this.ValidatesetupListAzureOpenAIEndpoint() ||
          !this.ValidateFastMode()
        ) {
          this.$swal.fire({
            icon: "warning",
            title: this.$t("warning"),
            text: this.$t("warningFillAll"),
            showConfirmButton: false,
            timer: 1800,
          });
          return;
        }
        if(!this.ValidateMailConfig()) {
          this.$swal.fire({
            icon: "warning",
            title: this.$t("warning"),
            text: this.$t("warningEmailConfig"),
            showConfirmButton: false,
            timer: 1800,
          });
          return;
        }
        this.dialog = true;
        this.MapSetupList(this.setupList, true);
        let req = {
          listDbsettings: this.setupList,
          listAzureOpenAIEndpoint: this.setupListAzureOpenAIEndpoint,
          listOpenIDConnect: this.setupOpenIDConnectList,
        };
        this.$call.GetSetupUpdateGeneral(
          req,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.$swal.fire({
                title: this.$t("successfullySaved"),
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  popup: 'success-banner',
                  title: 'success-banner-title'
                },
              });
              this.Get();
            }
            this.dialog = false;
          },
          (errorResource) => {
            this.dialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "UpdateUser");
      }
    },
    isNumber: function (evt) {
      evt = evt || window.event;
      let charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    copyClipboard() {
      this.$refs.clone.focus();
      document.execCommand("copy");
    },
    copyClipboardFullSteam() {
      this.$refs.cloneFullSteam.focus();
      document.execCommand("copy");
    },
    uploadFile(e) {
      if (e.length > 0) {
        switch (e[0].type) { //NOSONAR
          case "application/json":
            this.tempFileJSON = e[0];
            this.readFile(this.tempFileJSON);
            break;
          default:
          // code block
        }
      }
    },
    readFile(file) {
      const reader = new FileReader();
      reader.readAsText(file); //NOSONAR
      reader.onload = () => {
        this.setup.fileJSON = reader.result;
        this.isUploadFileJSON = false;
      };
      reader.onerror = () => {
        alert(this.$t("failedToReadFile"));
      };
    },
    Delete(index) {
      this.$swal
        .fire({
          title: this.$t("confirmDeletion"),
          text: this.$t("_DeleteItemConfirm"),
          icon: "warning",
          showCloseButton: true,
          showCancelButton: true,
          cancelButtonText: this.$t("cancel"),
          confirmButtonText: `<span class="swal-delete-btn-content"><img src="${deleteAlertIcon}" alt="" class="swal-delete-btn-icon" />${this.$t("delete")}</span>`,
          reverseButtons: false,
          focusCancel: true,
          buttonsStyling: false,
          customClass: {
            popup: "swal-delete-popup",
            title: "swal-delete-title",
            htmlContainer: "swal-delete-body",
            actions: "swal-delete-actions",
            confirmButton: "swal-delete-confirm",
            cancelButton: "swal-delete-cancel",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.setupListAzureOpenAIEndpoint.splice(index, 1);
          }
        });
    },
    Add() {
      let temp = {
        id: 0,
        azureOpenAIUrl: null,
        azureOpenAIAPIKey: null,
        priorityDisplay: null,
        showValue: false,
      };
      this.setupListAzureOpenAIEndpoint.push(temp);
    },
    AddOpenIDConnect(Provider) {
      let instance = null;
      if (Provider == "Microsoft") {
        instance = "https://login.microsoftonline.com/";
      }
      let temp = {
        id: 0,
        provider: Provider,
        sso: false,
        ssoChat: false,
        instance: instance,
        domain: null,
        tenantId: null,
        clientId: null,
        callbackPath: null,
        clientSecret: null,
        apiScope: null,
        urlConfig: null,
      };
      this.setupOpenIDConnectList.push(temp);
    },

    async GetDDLModelDataAzureProvider() {
      this.dialog = true;
      await this.$call.GetDDLModelDataAzureProvider(
        async (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDModelShow = dataResponse.data;
          } else {
            this.DDModelShow = [];
          }
          this.dialog = false;
        },
        (errorResource) => {
          console.error(errorResource);
        }
      );
    },
  },
};
</script>

<style scoped>
.setup-container {
  background: white !important;
  min-height: 100vh;
}

.header-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-content {
  color: white;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 300;
  margin: 0;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.date-display {
  color: white;
  font-size: 1.1rem;
  opacity: 0.9;
}

.save-btn {
  background: rgba(0, 0, 0, 0.2) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white !important;
}

.config-grid {
  border-radius: 16px !important;

  background: white !important;
  overflow: hidden;
  padding: 20px 20px 5px 20px;
}

.config-card {
  border-radius: 16px !important;
  border: none;
  background: white;
  transition: all 0.3s ease;
  width: 100%;
}

.config-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.card-header {
  background: linear-gradient(135deg, #0e81df 30%, #e2e8f0 100%);
  border-radius: 16px 16px 0 0 !important;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.card-header .v-card__title {
  font-size: 1.3rem;
  font-weight: 500;
  color: #495057;
}

.card-content {
  padding: 24px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title-add-btn {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: "";
  width: 4px;
  height: 20px;
  background: #667eea;
  margin-right: 12px;
  border-radius: 2px;
}

.section-title-add-btn::before {
  content: "";
  width: 4px;
  height: 20px;
  background: #667eea;
  margin-right: 12px;
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 16px;
}

.endpoint-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e9ecef;
}

.priority-field {
  max-width: 120px;
}

.toggle-btn,
.delete-btn {
  background: rgba(108, 117, 125, 0.1) !important;
}

.add-endpoint-btn {
  color: #C32E0E !important;
  height: 48px;
  transition: all 0.3s ease;
  border-radius: 16px;
  border: 1px solid #C32E0E !important;
  background: #FFF;
  box-shadow: unset !important;
  text-transform: none !important;
}

.add-endpoint-btn:hover {
  border-color: #C32E0E !important;
  background: rgba(195, 46, 14, 0.05) !important;
}

.storage-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.storage-section-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.storage-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.gpt-config-section {
  margin-bottom: 16px;
}

.service-tabs .v-tabs {
  border-radius: 12px;
  background: #f8f9fa;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.setting-item {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
}

.switch-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #495057;
}

.modern-switch .v-input__slot {
  background: transparent !important;
}

.loading-card {
  border-radius: 16px !important;
  text-align: center;
}

.loading-text {
  font-size: 1.1rem;
  color: #495057;
  font-weight: 500;
}

/* Form field styling */
.v-text-field--outlined {
  border-radius: 12px;
}

.v-text-field--outlined .v-input__control .v-input__slot {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .header-section {
    flex-direction: column;
    text-align: center;
    gap: 24px;
  }

  .header-actions {
    flex-direction: column;
    gap: 16px;
  }

  .page-title {
    font-size: 2rem;
  }
}

@media (max-width: 600px) {
  .setup-container .v-container {
    padding: 16px;
  }

  .header-section {
    padding: 24px;
  }

  .config-card {
    margin-bottom: 16px;
  }

  .card-content {
    padding: 16px;
  }
}

.save-action-card {
  border: 1px solid rgba(24, 103, 192, 0.12);
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 0 rgba(24, 103, 192, 0.15) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.save-action-content {
  padding: 20px 24px !important;
}

.save-action-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.save-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.save-text {
  display: flex;
  flex-direction: column;
}

.save-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1867c0;
  margin: 0;
  line-height: 1.2;
}

.save-subtitle {
  font-size: 0.875rem;
  color: #6c757d;
  margin: 2px 0 0 0;
  line-height: 1.3;
}

.save-action-button {
  min-width: 200px !important;
  height: 48px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.save-action-button:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 6px 16px rgba(24, 192, 60, 0.35) !important;
}

.save-action-button:active {
  transform: translateY(0) !important;
}

.save-action-button .v-btn__prepend {
  margin-right: 8px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .save-action-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .save-info {
    justify-content: center;
    text-align: center;
  }

  .save-action-button {
    width: 100%;
    min-width: unset !important;
  }
}

@media (max-width: 600px) {
  .save-action-content {
    padding: 16px 20px !important;
  }

  .save-title {
    font-size: 1rem;
  }

  .save-subtitle {
    font-size: 0.8rem;
  }

  .save-action-button {
    height: 44px !important;
    font-size: 0.9rem !important;
  }

  .save-info .v-icon {
    display: none;
  }
}

/* Loading state for save button */
.save-action-button.v-btn--loading {
  background: linear-gradient(135deg, #2ABF56 0%, #249E48 100%) !important;
}


.sticky-save-btn {
  position: fixed;
  top: 130px;
  right: 30px;
  z-index: 1000;
  max-width: 800px;
  box-shadow: 0px 6px 12px rgba(14, 87, 8, 0.4) !important;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
  animation: slideInFromRight 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    visibility: hidden;
    transform: translateX(100px);
  }

  to {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
  }
}

/* Enhanced hover effect สำหรับ sticky button */
.sticky-save-btn:hover {
  transform: translateX(-5px) scale(1.02);
  box-shadow: 0px 12px 20px rgba(14, 69, 133, 0.5) !important;
}

/* Responsive สำหรับ sticky button */
@media (max-width: 960px) {
  .sticky-save-btn {
    width: 200px;
    right: 20px;
    top: 120px;
  }
}

@media (max-width: 600px) {
  .sticky-save-btn {
    width: 180px;
    right: 15px;
    top: 100px;
    font-size: 0.9rem !important;
  }
}

/* ซ่อน sticky button เมื่อ dialog เปิด */
.v-overlay--active~.setup-container .sticky-save-btn {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}
</style>

<!-- Redesign batch css -->
<style scoped>
.image-icon-style {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  flex-shrink: 0;
  object-fit: contain;
}

.entra-field-label {
  margin-bottom: 10px;
}

/* Expandable Card Styles */
.config-card-expandable {
  background: transparent !important;
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
  overflow: hidden;
  border: 1px solid transparent;
  transition: border-color 0.3s ease;
}

.config-card-expanded {
  border: 1px solid #C9C9C9;
}

.expandable-card-header {
  background: linear-gradient(90deg, #FFF2F2 0%, #FFDED8 100%);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.3s ease;
  user-select: none;
  width: 100%;
  border: none;
  font: inherit;
  text-align: inherit;
  color: inherit;
  box-sizing: border-box;
}

.expandable-card-header:hover {
  background: linear-gradient(90deg, #FFE8E8 0%, #FFD0C8 100%);
}

.expandable-card-header:focus {
  outline-offset: 2px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.header-title-core-config {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  letter-spacing: 0.01em;
}

.toggle-icon-btn {
  color: #4a5568 !important;
  transition: transform 0.3s ease;
}

.toggle-icon-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

/* Card Content when expanded */
.config-card-expandable .card-content {
  background: #ffffff;
  padding: 5px 20px 15px 20px;
  border: 1px solid #f0e6e4;
  border-top: none;
  border-radius: 0 0 16px 16px;
}

/* Transition for expand/collapse */
.v-expand-transition {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active/Expanded state header styling */
.config-card-expandable:has(.card-content:not([style*="display: none"])) .expandable-card-header {
  border-radius: 16px 16px 0 0;
}

/* Accessibility focus styles */
.expandable-card-header:focus-visible {
  outline: 3px solid #667eea;
  outline-offset: 2px;
}

/* Field Label with Info Icon Styles */
.field-label-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.required-asterisk {
  color: #dc2626;
  font-weight: 600;
}

.info-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.info-icon:hover {
  opacity: 1;
}

/* Sync SSO Groups Card Styles */
.sync-sso-card {
  border-radius: 16px;
  padding: 10px 20px;
  height: 100%;
  max-width: 420px !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.sync-sso-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.sync-sso-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.sync-sso-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sync-sso-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
}

/* SSO Toggle Switch Styles */
.sso-toggle {
  margin: 0 4px;
}

.sso-toggle :deep(.v-switch__track) {
  transform: scale(0.8);
  width: 40px;
  height: 28px;
  border-radius: 999px;
  border: 2px solid transparent;
  background-color: transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.sso-toggle :deep(.v-switch__thumb) {
  width: 20px;
  height: 20px;
  box-shadow: none;
  border: 2px solid transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.sso-toggle--active :deep(.v-switch__track) {
  background-color: #ffffff !important;
  border-color: #d32f2f;
}

.sso-toggle--active :deep(.v-switch__thumb) {
  background-color: #d32f2f !important;
  border-color: #d32f2f;
  transform: translateX(-0.5px);
}

.sso-toggle--inactive :deep(.v-switch__track) {
  background-color: #bdbdbd !important;
  border-color: #bdbdbd;
}

.sso-toggle--inactive :deep(.v-switch__thumb) {
  background-color: #ffffff !important;
  border-color: #bdbdbd;
  width: 30px;
  height: 30px;
}

/* SSO Tabs Container Styles */
.sso-tabs-container {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 8px 0;
  border-bottom: 2px solid #e5e7eb;
}

.sso-tab-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
}

.sso-tab-item:hover {
  background: rgba(0, 0, 0, 0.03);
}

.sso-tab-item:focus {
  outline-offset: 2px;
}

.sso-tab-item::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
  border-radius: 2px 2px 0 0;
  transition: background 0.2s ease;
}

.sso-tab-item.sso-tab-active::after {
  background: #dc2626;
}

.sso-tab-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.2s ease;
  border-color: unset !important;
}

.sso-tab-icon-wrapper.sso-tab-icon-active {
  background-color: #dc2626;
  border-color: unset !important;
}

.sso-tab-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  transition: filter 0.2s ease;
}

.sso-tab-icon.sso-tab-icon-white {
  filter: brightness(0) invert(1);
}

.sso-tab-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
  transition: color 0.2s ease;
}

.sso-tab-label.sso-tab-label-active {
  color: #dc2626;
  font-weight: 600;
}

/* v-window has overflow:hidden; the inner v-row's default negative gutters
   (-12px) overflow the left edge and get clipped, cutting off the left
   column's label/input on the Authentik/Microsoft tabs. Absorb the negative
   margin with matching inline padding so both columns keep a symmetric inset. */
.sso-window {
  padding-inline: 12px;
}

/* table base */
:deep(thead) {
  background-color: #FFF2F2 !important;
  padding: 0 8px;
}

.v-table--has-top :deep(.v-table__wrapper) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-left .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-center .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}
</style>
